import { useConfig } from "@/hooks/useConfig";
import { MapPin, Phone, Mail, Navigation, Clock } from "lucide-react";

export default function Localizacao() {
  const cfg = useConfig();

  // Fallbacks (mesmos defaults do .env) garantem que a seção nunca aparece vazia.
  const endereco = cfg.endereco || "R. Camboja, 74 - Marcos Freire, Jaboatão dos Guararapes - PE, 54360-109";
  const telefone = cfg.telefone || "(81) 99985-7209";
  const email = cfg.contact_email || "nossoga@institutogaa.com.br";
  const whatsappUrl = cfg.whatsapp_url || "https://wa.me/5581999857209";
  const embedUrl =
    cfg.maps.embed_url ||
    `https://www.google.com/maps?q=${encodeURIComponent(endereco)}&output=embed`;
  const directionsUrl =
    cfg.maps.directions_url ||
    `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(endereco)}`;

  return (
    <section
      id="localizacao"
      data-testid="localizacao-section"
      className="section-pad bg-background"
    >
      <div className="container-ga">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div className="max-w-2xl">
            <div className="pill border-ga-emerald/30 text-ga-emerald_deep bg-ga-emerald/10 mb-5" data-testid="localizacao-pill">
              <MapPin className="w-3.5 h-3.5" />
              Nossa Localização
            </div>
            <h2 className="font-display font-black tracking-tighter text-3xl sm:text-4xl lg:text-5xl leading-[1.05] text-ga-ink">
              Venha nos <span className="gradient-text-ga">visitar</span>.
            </h2>
            <p className="mt-5 text-ga-ink/65 text-base sm:text-lg">
              Recepção aberta de segunda a sábado. Traga quem você quer que
              conheça o Instituto. Aqui, a porta sempre estará aberta.
            </p>
          </div>
          <a
            href={directionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="localizacao-how-to-get"
            className="self-start inline-flex items-center gap-2 rounded-full bg-ga-emerald text-white font-bold py-3.5 px-7 shadow-lg hover:bg-ga-emerald_deep transition-colors"
          >
            <Navigation className="w-5 h-5" /> Como chegar
          </a>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Map embed */}
          <div className="lg:col-span-7">
            <div
              className="relative rounded-3xl overflow-hidden border border-border bg-ga-ink/5 aspect-[4/3] sm:aspect-[16/10]"
              data-testid="localizacao-map-wrapper"
            >
              <iframe
                src={embedUrl}
                title="Mapa do Instituto GA+ na R. Camboja, 74, Jaboatão dos Guararapes, PE"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
                className="absolute inset-0 w-full h-full border-0"
                data-testid="localizacao-map-iframe"
              />
            </div>
          </div>

          {/* Info */}
          <aside className="lg:col-span-5 space-y-3" data-testid="localizacao-info">
            <div className="rounded-3xl bg-white border border-border p-6">
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-12 h-12 rounded-2xl bg-ga-emerald/10 text-ga-emerald_deep flex items-center justify-center">
                  <MapPin className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-[0.2em] text-ga-ink/55 font-bold">
                    Endereço
                  </div>
                  <div className="mt-1 font-bold text-ga-ink leading-snug" data-testid="localizacao-address">
                    {endereco}
                  </div>
                </div>
              </div>
            </div>

            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="localizacao-whatsapp"
              className="group flex items-center gap-4 rounded-3xl bg-white border border-border p-6 hover:border-ga-emerald transition-colors"
            >
              <div className="shrink-0 w-12 h-12 rounded-2xl bg-[#25D366]/10 text-[#128C7E] flex items-center justify-center group-hover:bg-[#25D366] group-hover:text-white transition-colors">
                <Phone className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs uppercase tracking-[0.2em] text-ga-ink/55 font-bold">
                  Telefone / WhatsApp
                </div>
                <div className="mt-1 font-bold text-ga-ink" data-testid="localizacao-phone">
                  {telefone}
                </div>
              </div>
            </a>

            <a
              href={`mailto:${email}`}
              data-testid="localizacao-email"
              className="group flex items-center gap-4 rounded-3xl bg-white border border-border p-6 hover:border-ga-ink/30 transition-colors"
            >
              <div className="shrink-0 w-12 h-12 rounded-2xl bg-ga-ink/5 text-ga-ink flex items-center justify-center">
                <Mail className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs uppercase tracking-[0.2em] text-ga-ink/55 font-bold">
                  E-mail
                </div>
                <div className="mt-1 font-bold text-ga-ink truncate">
                  {email}
                </div>
              </div>
            </a>

            <div className="rounded-3xl bg-ga-ink text-white p-6" data-testid="localizacao-hours">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-ga-amber" />
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-ga-amber">
                  Horário de atendimento
                </div>
              </div>
              <div className="mt-3 space-y-1 text-sm text-white/85">
                <div>Segunda a sexta: 8h às 18h.</div>
                <div>Sábado: 8h às 12h.</div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
